import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import { useGetMemorabiliaBySlug } from 'hooks';
// Api
import { GET_MEMORABILIA_BY_SLUG } from 'api/memorabilia/queries';
import {
  GetMemorabiliaBySlug,
  GetMemorabiliaBySlugVariables,
} from 'api/memorabilia/types/GetMemorabiliaBySlug';
import { MemorabiliaStatus } from 'api/graphql-global-types';
// Helpers
import { getCanonicalUrl } from 'helpers/canonical';
import {
  ApolloPageContext,
  PageProps,
  getCleanQueryParam,
  markPageAsNotFound,
} from 'helpers/next-page';
import {
  cleanDescription,
  generateProductStructuredData,
} from 'helpers/structured-data';
// Layouts
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// UI
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';
// Components
import Memorabilia from 'components/Memorabilia/Memorabilia';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';
import RouteRedirect from 'components/RouteRedirect/RouteRedirect';

const MemorabiliaPageContent = (): JSX.Element => {
  const { data, refetch } = useGetMemorabiliaBySlug();
  const { asPath } = useRouter();

  const memorabilia = data?.getMemorabiliaBySlug;

  if (!memorabilia) {
    return <FourDotsLoader />;
  }

  const storeName = memorabilia.store?.storeDetails?.storeName || '';
  const productName = memorabilia.title || '';

  const metaTitle = `${productName} | MILLIONS`;

  const metaDescription = (() => {
    const MAX = 160;
    const cleanDesc = cleanDescription(memorabilia.description)
      .replace(/https?:\/\/\S+/g, '')
      .replace(/\bAvailable exclusively on MILLIONS\.?\s*/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

    const price =
      typeof memorabilia.price === 'number' && memorabilia.price > 0
        ? ` $${memorabilia.price}.`
        : '';
    const attribution = `${
      storeName ? `by ${storeName} ` : ''
    }on MILLIONS.${price}`;

    const sentences = cleanDesc.match(/[^.!?]+[.!?]+\s*/g) || [];
    let body = '';
    for (const s of sentences) {
      const next = body ? `${body}${s}` : s.trim();
      if (`${next} ${attribution}`.length <= MAX) body = next;
      else if (!body) {
        body = s.trim();
        break;
      } else break;
    }

    return body
      ? `${body.trim()} ${attribution}`.trim()
      : `${productName}${
          storeName ? ` by ${storeName}` : ''
        } on MILLIONS.${price}`;
  })();
  const imageUrl =
    memorabilia.images?.find((img) => img.isMainImage)?.url ?? '';

  const canonicalUrl = getCanonicalUrl(asPath);

  return (
    <>
      <Head
        title={metaTitle}
        description={metaDescription}
        image={imageUrl}
        canonicalLink={canonicalUrl}
        structuredData={generateProductStructuredData(
          memorabilia,
          'memorabilia'
        )}
      />
      <Header />
      <Main withoutSidesPadding>
        <Memorabilia memorabilia={memorabilia} refetch={refetch} />
      </Main>
      <Footer />
    </>
  );
};

const MemorabiliaPage: NextPage<PageProps> = ({ isNotFound, redirectTo }) => {
  if (redirectTo) {
    return <RouteRedirect to={redirectTo} />;
  }

  if (isNotFound) {
    return <NotFoundPage />;
  }

  return <MemorabiliaPageContent />;
};

MemorabiliaPage.getInitialProps = async (
  ctx: ApolloPageContext
): Promise<PageProps> => {
  const athleteSlug = getCleanQueryParam(ctx.query.athleteSlug);
  const memorabiliaSlug = getCleanQueryParam(ctx.query.memorabiliaSlug);

  if (!ctx.apolloClient || !athleteSlug || !memorabiliaSlug) {
    return {};
  }

  try {
    const { data } = await ctx.apolloClient.query<
      GetMemorabiliaBySlug,
      GetMemorabiliaBySlugVariables
    >({
      query: GET_MEMORABILIA_BY_SLUG,
      variables: {
        slug: memorabiliaSlug,
        storeSlug: athleteSlug,
      },
    });

    const memorabilia = data?.getMemorabiliaBySlug;

    if (!memorabilia) {
      markPageAsNotFound(ctx);
      return { isNotFound: true };
    }

    if (
      memorabilia.status === MemorabiliaStatus.Removed &&
      memorabilia.store?.slug
    ) {
      const redirectTo = `/${memorabilia.store.slug}/memorabilia`;

      if (ctx.res) {
        ctx.res.writeHead(301, { Location: redirectTo });
        ctx.res.end();
        return { redirectTo };
      }

      return { redirectTo };
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { section: 'ssr', page: 'memorabilia/[memorabiliaSlug]' },
    });
    markPageAsNotFound(ctx);
    return { isNotFound: true };
  }

  return {};
};

export default withApollo(MemorabiliaPage, { getDataFromTree });
